import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Offer, LoadingState } from '../../types';
import { offerService } from '../../services/offerService';

interface OfferState extends LoadingState {
  offers: Offer[];
  selectedOffer: Offer | null;
}

const initialState: OfferState = {
  offers: [],
  selectedOffer: null,
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchOffers = createAsyncThunk(
  'offers/fetchOffers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await offerService.getOffers();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch offers');
    }
  }
);

export const fetchOfferById = createAsyncThunk(
  'offers/fetchOfferById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await offerService.getOfferById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch offer');
    }
  }
);

const offerSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedOffer: (state, action: PayloadAction<Offer | null>) => {
      state.selectedOffer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Offers
      .addCase(fetchOffers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOffers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.offers = action.payload;
      })
      .addCase(fetchOffers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Offer by ID
      .addCase(fetchOfferById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOfferById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedOffer = action.payload;
      })
      .addCase(fetchOfferById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setSelectedOffer } = offerSlice.actions;
export default offerSlice.reducer;
